// Application constants

// API Configuration - dynamic base URL based on environment/domain
const resolveApiBaseUrl = () => {
  // Highest priority: explicit env variable
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }

  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;

    // Production domain
    if (hostname === 'rrrealty.ai' || hostname === 'www.rrrealty.ai') {
      return 'https://site-net-ajdhc8cngbgrfbhe.centralus-01.azurewebsites.net';
    }

    // Testing domain
    if (hostname === 'testing.rrrealty.ai') {
      return 'https://site-net-rrai-blue-fsgabaardkdhhnhf.centralus-01.azurewebsites.net';
    }

    // Local development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:7071/api';
    }
  }

  // Fallback to testing endpoint if nothing else matches
  return 'https://site-net-rrai-blue-fsgabaardkdhhnhf.centralus-01.azurewebsites.net';
};

export const API_CONFIG = {
  BASE_URL: resolveApiBaseUrl(),
  TIMEOUT: parseInt(import.meta.env.VITE_API_TIMEOUT || '30000'),
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000,
} as const;

// File Upload Configuration
export const FILE_CONFIG = {
  MAX_SIZE_MB: parseInt(import.meta.env.VITE_MAX_FILE_SIZE_MB || '25'),
  ALLOWED_TYPES: [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    'text/csv',
    'text/plain',
  ],
  ALLOWED_EXTENSIONS: ['.pdf', '.docx', '.xlsx', '.xls', '.csv', '.txt'],
} as const;

// UI Configuration
export const UI_CONFIG = {
  ANIMATION_DURATION: 200,
  DEBOUNCE_DELAY: 300,
  TOAST_DURATION: 5000,
  MAX_MESSAGE_LENGTH: 4000,
  MESSAGES_PER_PAGE: 50,
} as const;

// Local Storage Keys
export const STORAGE_KEYS = {
  CHAT_SESSIONS: 'rr_realty_chat_sessions',
  USER_PREFERENCES: 'rr_realty_user_preferences',
  THEME: 'rr_realty_theme',
  LAST_SESSION: 'rr_realty_last_session',
} as const;

// Theme Configuration
export const THEME = {
  COLORS: {
    PRIMARY: '#165540',
    PRIMARY_LIGHT: '#b4ceb3',
    PRIMARY_DARK: '#013220',
    SECONDARY: '#3a668c',
    SECONDARY_LIGHT: '#b6ccd7',
    SECONDARY_DARK: '#192839',
    ACCENT: '#e6b751',
    ACCENT_LIGHT: '#f2d084',
    ACCENT_DARK: '#d49e2a',
  },
  BREAKPOINTS: {
    MOBILE: '768px',
    TABLET: '1024px',
    DESKTOP: '1200px',
  },
} as const;

// Error Messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error. Please check your connection and try again.',
  AUTHENTICATION_ERROR: 'Authentication failed. Please log in again.',
  FILE_TOO_LARGE: `File size exceeds ${FILE_CONFIG.MAX_SIZE_MB}MB limit.`,
  INVALID_FILE_TYPE: 'Invalid file type. Supported formats: PDF, Word (.docx), Excel (.xlsx/.xls), CSV, plain text.',
  CHAT_ERROR: 'Failed to send message. Please try again.',
  SESSION_ERROR: 'Failed to load chat session. Please refresh the page.',
  GENERIC_ERROR: 'Something went wrong. Please try again.',
} as const;

// Success Messages
export const SUCCESS_MESSAGES = {
  FILE_UPLOADED: 'File uploaded successfully!',
  MESSAGE_SENT: 'Message sent successfully!',
  SESSION_CREATED: 'New chat session created!',
  SESSION_DELETED: 'Chat session deleted successfully!',
  DOCUMENT_DELETED: 'Document deleted successfully!',
} as const;

// Feature Flags
export const FEATURES = {
  DOCUMENT_UPLOAD: import.meta.env.VITE_ENABLE_DOCUMENT_UPLOAD === 'true',
  CONVERSATION_SAVE: import.meta.env.VITE_ENABLE_CONVERSATION_SAVE === 'true',
  VOICE_INPUT: import.meta.env.VITE_ENABLE_VOICE_INPUT === 'true',
  DARK_MODE: import.meta.env.VITE_ENABLE_DARK_MODE === 'true',
} as const;

// QueryIntent is now the canonical routing path. Keep `VITE_USE_INTENT_ROUTING=false`
// as the emergency off switch, but default new builds to the LLM-backed intent route
// instead of the legacy hostname gate.
export const useNewIntentRouting = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (import.meta.env.VITE_USE_INTENT_ROUTING === 'false') return false;
  return true;
};

// Build-time model overrides. When set, the frontend includes `deployment` in
// the body of /api/enterprisegrounding and /api/queryintent requests, and the
// backend honours it against its allowlist. Lets test-slot and prod-slot builds
// target different Azure OpenAI deployments without touching shared backend
// env vars. When unset, backend env defaults apply.
//   Set via repo variables in GitHub Actions:
//     VITE_CHAT_DEPLOYMENT    — e.g. "gpt-5-4"
//     VITE_INTENT_DEPLOYMENT  — e.g. "gpt-5-4-mini"
export const getChatDeployment = (): string | undefined => {
  const v = import.meta.env.VITE_CHAT_DEPLOYMENT;
  return typeof v === 'string' && v.trim() ? v.trim() : undefined;
};

export const getIntentDeployment = (): string | undefined => {
  const v = import.meta.env.VITE_INTENT_DEPLOYMENT;
  return typeof v === 'string' && v.trim() ? v.trim() : undefined;
};

// Ephemeral backend-owned upload pipeline. Defaults off so production can keep
// the browser-local document path until the additive backend endpoints are
// deployed and validated.
export const useSessionDocuments = (): boolean => {
  return import.meta.env.VITE_SESSION_DOCUMENTS_ENABLED === 'true';
};

// Suggested prompts shown on the empty-state welcome screen are defined inline
// in ChatInterface.tsx (and are tuned to the chatbot's actual capabilities —
// meetings, emails, etc.). Earlier this file shipped a real-estate-flavoured
// constant block that was never imported anywhere; removed to avoid drift.
